import type { ReactNode, Ref } from 'react';
import { Icon } from './Icon.js';
import { Menu, MenuItem, MenuSection } from './Menu.js';
import './OrgSwitcher.css';

export interface OrgOption {
  id: string;
  name: string;
  /** Plan or role line under the name in the menu — the prototype shows the plan. */
  meta?: string;
}

export interface OrgSwitcherProps {
  orgs: OrgOption[];
  currentId: string;
  onSelect: (id: string) => void;
  /** Extra rows under the org list, e.g. "Organisation settings". */
  footer?: ReactNode;
  /**
   * The rail form: the square tile alone in a 36px circular button, with the name
   * and chevron dropped. The prototype keeps org identity visible when the nav is
   * collapsed rather than removing it — it moves to its own block under the
   * hamburger (`navIsRail`), which is why this is a variant of the same control
   * and not a separate component: the menu, the selection and the accessible name
   * are identical, only the trigger's shape changes.
   */
  collapsed?: boolean;
}

function OrgTile({ name }: { name: string }) {
  return (
    <span className="dpr-org-switcher__tile" aria-hidden="true">
      {name.trim().charAt(0).toUpperCase()}
    </span>
  );
}

/**
 * The organisation switcher (design-system catalogue, Tier 2) — top-left of the
 * sidebar, immediately right of the collapse hamburger.
 *
 * Replaces a native `<select>` in the topbar, which was wrong in three ways at
 * once (audit S-2): wrong control (a select cannot show a per-org tile, a plan
 * line, or a check on the current org), wrong place (the prototype puts org
 * identity in the sidebar header, not the topbar), and wrong size (it rendered at
 * the smallest available height for a primary global control — audit D-6).
 *
 * With a single org this renders as plain text rather than a control: the
 * prototype always shows *which* organisation you are in — for a product that
 * writes to a customer's CRM, that is not a detail to hide — but a button opening
 * an empty menu invites a click that does nothing.
 *
 * The tile is a rounded **square** while a person's avatar is a **circle**. That
 * is deliberate in the prototype and worth preserving: it is what stops the two
 * identities in the chrome (which org / which user) reading as the same kind of
 * thing.
 */
export function OrgSwitcher({ orgs, currentId, onSelect, footer, collapsed }: OrgSwitcherProps) {
  const current = orgs.find((o) => o.id === currentId) ?? orgs[0];
  if (!current) return null;

  const identity = collapsed ? (
    <OrgTile name={current.name} />
  ) : (
    <>
      <OrgTile name={current.name} />
      <span className="dpr-org-switcher__name">{current.name}</span>
    </>
  );
  const rootClass = collapsed ? 'dpr-org-switcher dpr-org-switcher--rail' : 'dpr-org-switcher';

  if (orgs.length === 1 && !footer) {
    return (
      <span className={rootClass} aria-label={`Organisation: ${current.name}`}>
        {identity}
      </span>
    );
  }

  return (
    <Menu
      label="Switch organisation"
      align="start"
      width={250}
      trigger={({ ref, ...triggerProps }) => (
        <button
          {...triggerProps}
          ref={ref as Ref<HTMLButtonElement>}
          type="button"
          className={rootClass}
          aria-label={`Organisation: ${current.name}. Switch organisation`}
        >
          {identity}
          {/* No chevron in the rail form: at 36px it would crowd the tile, and the
              prototype's rail button shows the tile alone. */}
          {collapsed ? null : (
            <span className="dpr-org-switcher__chevron" aria-hidden="true">
              <Icon name="chevronDown" size={14} />
            </span>
          )}
        </button>
      )}
    >
      <MenuSection>
        {orgs.map((org) => (
          <MenuItem
            key={org.id}
            selected={org.id === currentId}
            onSelect={() => onSelect(org.id)}
            icon={<OrgTile name={org.name} />}
            hint={org.id === currentId ? <Icon name="check" size={14} /> : undefined}
          >
            <span className="dpr-org-switcher__option">
              <span className="dpr-org-switcher__option-name">{org.name}</span>
              {org.meta ? <span className="dpr-org-switcher__option-plan">{org.meta}</span> : null}
            </span>
          </MenuItem>
        ))}
      </MenuSection>
      {footer ? <MenuSection>{footer}</MenuSection> : null}
    </Menu>
  );
}
